import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAffiliateTheme } from '../../context/AffiliateThemeContext';

const MarketingResources = ({ referralCode }) => {
  const { t } = useTranslation();
  const [selectedBannerSize, setSelectedBannerSize] = useState('horizontal');
  const [selectedBannerColor, setSelectedBannerColor] = useState('purple');

  const appLink = `https://xenya-face.app/download?ref=${referralCode}`;
  
  const bannerSizes = {
    horizontal: { width: 728, height: 90 },
    square: { width: 300, height: 250 },
    vertical: { width: 160, height: 600 },
    mobile: { width: 320, height: 100 }
  };
  
  // Utiliser les couleurs du thème pour les bannières
  const theme = useAffiliateTheme();
  const bannerColors = {
    purple: { 
      bg: theme.affiliate.gradients.primary, 
      text: theme.affiliate.colors.text.contrast.primary 
    },
    dark: { 
      bg: theme.affiliate.gradients.dark, 
      text: theme.affiliate.colors.text.contrast.dark 
    },
    light: { 
      bg: theme.affiliate.colors.background, 
      text: theme.affiliate.colors.text.primary,
      border: '1px solid ' + theme.affiliate.colors.border
    }
  };
  
  const handleBannerClick = (format) => {
    // Construire le chemin du fichier à télécharger
    const fileName = `xenya-affiliate-${selectedBannerSize}-${selectedBannerColor}.${format}`;
    const filePath = `/assets/affiliate-marketing/${fileName}`;
    
    // Créer un lien de téléchargement
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const copyEmbedCode = () => {
    const embedCode = `<a href="${appLink}" target="_blank" rel="noopener noreferrer">
  <img src="https://xenya-face.app/assets/affiliate-marketing/xenya-affiliate-${selectedBannerSize}-${selectedBannerColor}.png" 
       alt="Xenya - Analyse faciale avec IA" 
       width="${bannerSizes[selectedBannerSize].width}" 
       height="${bannerSizes[selectedBannerSize].height}" />
</a>`;
    
    navigator.clipboard.writeText(embedCode)
      .then(() => {
        alert(t('affiliate.marketing.codeCopied'));
      })
      .catch(err => {
        console.error('Erreur lors de la copie du code :', err);
      });
  };

  return (
    <ResourcesContainer>
      <SectionTitle>{t('affiliate.marketing.title')}</SectionTitle>
      <p>{t('affiliate.marketing.description')}</p>
      
      <ResourcesGrid>
        {/* Bannières publicitaires */}
        <ResourceCard
          as={motion.div}
          whileHover={{ y: -5 }}
          transition={{ duration: 0.2 }}
        >
          <ResourceHeader>
            <ResourceIcon>🖼️</ResourceIcon>
            <ResourceTitle>{t('affiliate.marketing.banners.title')}</ResourceTitle>
          </ResourceHeader>
          
          <p>{t('affiliate.marketing.banners.description')}</p>
          
          <BannerCustomization>
            <CustomizationGroup>
              <CustomizationLabel>{t('affiliate.marketing.banners.size')}:</CustomizationLabel>
              <SelectControl
                value={selectedBannerSize}
                onChange={(e) => setSelectedBannerSize(e.target.value)}
              >
                <option value="horizontal">{t('affiliate.marketing.banners.sizes.horizontal')} (728×90)</option>
                <option value="square">{t('affiliate.marketing.banners.sizes.square')} (300×250)</option>
                <option value="vertical">{t('affiliate.marketing.banners.sizes.vertical')} (160×600)</option>
                <option value="mobile">{t('affiliate.marketing.banners.sizes.mobile')} (320×100)</option>
              </SelectControl>
            </CustomizationGroup>
            
            <CustomizationGroup>
              <CustomizationLabel>{t('affiliate.marketing.banners.color')}:</CustomizationLabel>
              <ColorOptions>
                <ColorOption 
                  selected={selectedBannerColor === 'purple'}
                  onClick={() => setSelectedBannerColor('purple')}
                  color={bannerColors.purple.bg}
                />
                <ColorOption 
                  selected={selectedBannerColor === 'dark'}
                  onClick={() => setSelectedBannerColor('dark')}
                  color={bannerColors.dark.bg}
                />
                <ColorOption 
                  selected={selectedBannerColor === 'light'}
                  onClick={() => setSelectedBannerColor('light')}
                  color={bannerColors.light.bg}
                  border="1px solid #ddd"
                />
              </ColorOptions>
            </CustomizationGroup>
          </BannerCustomization>
          
          <BannerPreview
            style={{
              width: `${bannerSizes[selectedBannerSize].width / 2}px`,
              height: `${bannerSizes[selectedBannerSize].height / 2}px`,
              background: bannerColors[selectedBannerColor].bg,
              color: bannerColors[selectedBannerColor].text
            }}
            border={bannerColors[selectedBannerColor].border}
          >
            <span>Xenya - {t('affiliate.marketing.bannerPreview')}</span>
            <small>ref: {referralCode}</small>
          </BannerPreview>
          
          <ActionGroup>
            <ActionButton onClick={() => handleBannerClick('png')}>
              {t('affiliate.marketing.downloadPNG')}
            </ActionButton>
            <ActionButton onClick={copyEmbedCode}>
              {t('affiliate.marketing.copyCode')}
            </ActionButton>
          </ActionGroup>
        </ResourceCard>
        
        {/* Email Templates */}
        <ResourceCard
          as={motion.div}
          whileHover={{ y: -5 }}
          transition={{ duration: 0.2 }}
        >
          <ResourceHeader>
            <ResourceIcon>✉️</ResourceIcon>
            <ResourceTitle>{t('affiliate.marketing.email.title')}</ResourceTitle>
          </ResourceHeader>
          
          <p>{t('affiliate.marketing.email.description')}</p>
          
          <EmailPreview>
            <EmailTemplate>
              <strong>{t('affiliate.marketing.email.subject')}</strong>
              <p>{t('affiliate.marketing.email.template', { referralCode })}</p>
              <EmailLink href={appLink} target="_blank" rel="noopener noreferrer">
                {appLink}
              </EmailLink>
            </EmailTemplate>
          </EmailPreview>
          
          <ActionButton onClick={() => navigator.clipboard.writeText(t('affiliate.marketing.email.template', { referralCode }) + `\n\n${appLink}`)}>
            {t('affiliate.marketing.copyTemplate')}
          </ActionButton>
        </ResourceCard>
        
        {/* Social Media Templates */}
        <ResourceCard
          as={motion.div}
          whileHover={{ y: -5 }}
          transition={{ duration: 0.2 }}
        >
          <ResourceHeader>
            <ResourceIcon>📱</ResourceIcon>
            <ResourceTitle>{t('affiliate.marketing.social.title')}</ResourceTitle>
          </ResourceHeader>
          
          <p>{t('affiliate.marketing.social.description')}</p>
          
          <SocialTemplates>
            <SocialTemplate>
              <SocialPlatform><i className="fab fa-instagram"></i> Instagram</SocialPlatform>
              <SocialContent>
                {t('affiliate.marketing.social.instagram', { referralCode })}
              </SocialContent>
              <ActionButtonSmall onClick={() => navigator.clipboard.writeText(t('affiliate.marketing.social.instagram', { referralCode }) + `\n\n${appLink}`)}>
                {t('affiliate.marketing.copy')}
              </ActionButtonSmall>
            </SocialTemplate>
            
            <SocialTemplate>
              <SocialPlatform><i className="fab fa-facebook"></i> Facebook</SocialPlatform>
              <SocialContent>
                {t('affiliate.marketing.social.facebook', { referralCode })}
              </SocialContent>
              <ActionButtonSmall onClick={() => navigator.clipboard.writeText(t('affiliate.marketing.social.facebook', { referralCode }) + `\n\n${appLink}`)}>
                {t('affiliate.marketing.copy')}
              </ActionButtonSmall>
            </SocialTemplate>
            
            <SocialTemplate>
              <SocialPlatform><i className="fab fa-twitter"></i> Twitter</SocialPlatform>
              <SocialContent>
                {t('affiliate.marketing.social.twitter', { referralCode })}
              </SocialContent>
              <ActionButtonSmall onClick={() => navigator.clipboard.writeText(t('affiliate.marketing.social.twitter', { referralCode }) + `\n\n${appLink}`)}>
                {t('affiliate.marketing.copy')}
              </ActionButtonSmall>
            </SocialTemplate>
          </SocialTemplates>
        </ResourceCard>
        
        {/* Flyers imprimables */}
        <ResourceCard
          as={motion.div}
          whileHover={{ y: -5 }}
          transition={{ duration: 0.2 }}
        >
          <ResourceHeader>
            <ResourceIcon>🖨️</ResourceIcon>
            <ResourceTitle>{t('affiliate.marketing.print.title')}</ResourceTitle>
          </ResourceHeader>
          
          <p>{t('affiliate.marketing.print.description')}</p>
          
          <PrintOptions>
            <PrintOption>
              <PrintOptionTitle>{t('affiliate.marketing.print.flyer')}</PrintOptionTitle>
              <PrintOptionDescription>
                {t('affiliate.marketing.print.flyerDesc')}
              </PrintOptionDescription>
              <ActionButton onClick={() => window.open(`/assets/affiliate-marketing/xenya-flyer-${referralCode}.pdf`, '_blank')}>
                {t('affiliate.marketing.downloadPDF')}
              </ActionButton>
            </PrintOption>
            
            <PrintOption>
              <PrintOptionTitle>{t('affiliate.marketing.print.businessCard')}</PrintOptionTitle>
              <PrintOptionDescription>
                {t('affiliate.marketing.print.businessCardDesc')}
              </PrintOptionDescription>
              <ActionButton onClick={() => window.open(`/assets/affiliate-marketing/xenya-business-card-${referralCode}.pdf`, '_blank')}>
                {t('affiliate.marketing.downloadPDF')}
              </ActionButton>
            </PrintOption>
          </PrintOptions>
        </ResourceCard>
      </ResourcesGrid>
      
      <ResourceTips>
        <h3>{t('affiliate.marketing.tips.title')}</h3>
        <TipsList>
          <Tip>{t('affiliate.marketing.tips.tip1')}</Tip>
          <Tip>{t('affiliate.marketing.tips.tip2')}</Tip>
          <Tip>{t('affiliate.marketing.tips.tip3')}</Tip>
          <Tip>{t('affiliate.marketing.tips.tip4')}</Tip>
        </TipsList>
      </ResourceTips>
    </ResourcesContainer>
  );
};

// Styled Components
const ResourcesContainer = styled.div`
  margin-top: 30px;
`;

const SectionTitle = styled.h2`
  color: #333;
  margin-bottom: 10px;
`;

const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ResourceCard = styled.div`
  background-color: ${({ theme }) => theme.affiliate.colors.card};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.affiliate.cardShadow};
  padding: 20px;
  
  p {
    color: ${({ theme }) => theme.affiliate.colors.text.secondary};
    margin-bottom: 15px;
  }
`;

const ResourceHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const ResourceIcon = styled.div`
  font-size: 1.5rem;
  margin-right: 10px;
`;

const ResourceTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const BannerCustomization = styled.div`
  margin-bottom: 15px;
`;

const CustomizationGroup = styled.div`
  margin-bottom: 10px;
`;

const CustomizationLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.affiliate.colors.text.primary};
`;

const SelectControl = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.affiliate.colors.border};
  border-radius: 5px;
  
  &:focus {
    border-color: ${({ theme }) => theme.affiliate.colors.primary};
    outline: none;
  }
`;

const ColorOptions = styled.div`
  display: flex;
  gap: 10px;
`;

const ColorOption = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: ${props => props.color};
  border: ${props => props.border || 'none'};
  outline: ${props => props.selected ? `2px solid ${props.theme.affiliate.colors.primary}` : 'none'};
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const BannerPreview = styled.div`
  margin: 15px 0;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.affiliate.cardShadow};
  ${props => props.border && `border: ${props.border};`}
  
  span {
    display: block;
    margin-bottom: 5px;
  }
  
  small {
    font-size: 0.8rem;
    opacity: 0.8;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.affiliate.gradients.primary};
  color: ${({ theme }) => theme.affiliate.colors.text.contrast.primary};
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ActionButtonSmall = styled(ActionButton)`
  padding: 5px 10px;
  font-size: 0.8rem;
`;

const EmailPreview = styled.div`
  background-color: ${({ theme }) => theme.affiliate.colors.cardAlt};
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 15px;
  border: 1px solid ${({ theme }) => theme.affiliate.colors.border};
`;

const EmailTemplate = styled.div`
  p {
    margin: 10px 0;
  }
`;

const EmailLink = styled.a`
  color: ${({ theme }) => theme.affiliate.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SocialTemplates = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SocialTemplate = styled.div`
  background-color: ${({ theme }) => theme.affiliate.colors.cardAlt};
  border-radius: 5px;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.affiliate.colors.border};
`;

const SocialPlatform = styled.div`
  font-weight: 500;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.affiliate.colors.text.primary};
  
  i {
    margin-right: 5px;
  }
`;

const SocialContent = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.affiliate.colors.text.secondary};
  margin-bottom: 10px;
`;

const PrintOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const PrintOption = styled.div`
  background-color: ${({ theme }) => theme.affiliate.colors.cardAlt};
  border-radius: 5px;
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.affiliate.colors.border};
`;

const PrintOptionTitle = styled.h4`
  margin: 0 0 10px 0;
  color: ${({ theme }) => theme.affiliate.colors.text.primary};
`;

const PrintOptionDescription = styled.p`
  font-size: 0.9rem;
  margin-bottom: 15px !important;
`;

const ResourceTips = styled.div`
  margin-top: 30px;
  background-color: ${({ theme }) => theme.affiliate.colors.card};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.affiliate.cardShadow};
  padding: 20px;
  
  h3 {
    color: ${({ theme }) => theme.affiliate.colors.text.primary};
    margin-bottom: 15px;
  }
`;

const TipsList = styled.ul`
  padding-left: 20px;
`;

const Tip = styled.li`
  color: ${({ theme }) => theme.affiliate.colors.text.secondary};
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export default MarketingResources;